import { Audited, BaseEntity, ID, User } from '@ianesthesie-kit/services';

export enum SurgicalEventStatus {
  INITIALIZED = 'INITIALIZED',
  PENDING_QUESTIONNAIRE_COMPLETION = 'PENDING_QUESTIONNAIRE_COMPLETION',
  FINALIZED = 'FINALIZED'
}

export interface Patient extends User {
  clinicalTrialOptOut?: boolean;
}

export interface SurgicalEvent extends Audited {
  active: boolean;
  patient: Patient;
  hospitalId: ID;
  status: SurgicalEventStatus;
}

export type Risk = 'LOW' | 'MEDIUM' | 'HIGH';
export type SurgerySeverity =
  | Risk
  | 'VERY_LOW'
  | 'VERY_HIGH'
  | 'NOT_APPLICABLE';

const severityOrder: Record<SurgerySeverity, number> = {
  NOT_APPLICABLE: 0,
  VERY_LOW: 1,
  LOW: 2,
  MEDIUM: 3,
  HIGH: 4,
  VERY_HIGH: 5
};

export function sortSeverity(
  a: SurgerySeverity | undefined,
  b: SurgerySeverity | undefined
) {
  if (!a && !b) {
    return 0;
  }
  if (!a) {
    return 1;
  }
  if (!b) {
    return -1;
  }

  return severityOrder[a] - severityOrder[b];
}

export function maxSeverity(...severities: (SurgerySeverity | undefined)[]) {
  return (
    severities?.filter(Boolean).sort(sortSeverity).at(-1) ?? 'NOT_APPLICABLE'
  );
}

export interface Surgery extends BaseEntity {
  code: string;
  translationCode: string;
  severity: SurgerySeverity;
}

export function isRisk(e: any): e is Risk {
  return !!e && (e === 'HIGH' || e === 'MEDIUM' || e === 'LOW');
}

export interface SurgeryInfo extends BaseEntity {
  prepScore?: number;
  emergency?: string;
  surgeryDate?: string;
  anesthesiaConsultationDate?: string;
  careType?: string;
  surgerySide?: string;
  surgeries?: Surgery[];
  surgeryRisk?: SurgerySeverity;
  userDefinedSurgeries?: string[];
  surgeons?: string[];
  anesthesiologists?: string[];
  anesthesiaRisk?: Risk;
  intensiveCareAdmissionRisk?: Risk;
}

/** A surgery planned at the consultation whose surgical event does not exist yet. */
export interface UpcomingSurgery {
  planId: string;
  surgeryDate: string;
  surgerySide?: string;
  surgeries?: Surgery[];
  userDefinedSurgeries?: string[];
}

export interface AnesthesiaRisk extends BaseEntity {
  intubation?: Risk;
  ventilation?: Risk;
  aspiration?: Risk;
  hypotension?: Risk;
  hemorrhage?: Risk;
  ponv?: Risk;
  delirium?: Risk;
  dental?: Risk;
  transfusion?: Risk;
  kidney?: Risk;
  cardiac?: Risk;
  hepatic?: Risk;
  infection?: Risk;
  postOpPain?: Risk;
  phlebitis?: Risk;
  allergy?: Risk;
}
